// components/modals/AddTaskModal.tsx - Reusable Add Task Modal
import React, { useState, useEffect } from 'react';
import { X, Bot, Play, Loader2, AlertTriangle, Users, Link2 } from 'lucide-react';
import { taskService, CreateTaskData } from '../../services/taskApi.ts';
import { customAgentAPI, spacesAPI } from '../../services/api.js';
import { isChannelConnected, CHANNEL_LABELS } from '../../services/notificationChannels.js';
import toast from 'react-hot-toast';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskAdded: (task: any) => void;
  mode: 'task-management' | 'weekly-planner' | 'space-task';
  currentWeekDates?: Date[];
  defaultDay?: number;
  task?: Partial<TaskFormData>; // Add this prop for edit mode
  spaceId?: string; // required when mode === 'space-task'
  initialDueDate?: string; // ISO date — pre-fills the due date field (e.g. from a calendar-jump click)
  presetAgentId?: string; // pre-selects the agent picker (task-management mode) — e.g. opening this modal from that agent's own page
}

interface TaskFormData {
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High';
  category: 'App Idea' | 'Web App' | 'Social App' | 'EdTech' | 'Business Idea' | 'Product Concept' | 'Feature Request' | 'Research Topic' | 'Creative Project' | 'Other';
  estimatedTime: '0.5h' | '1h' | '2h' | '4h' | '8h';
  tags: string;
  dueDate?: string; // For task management
  day?: number; // For weekly planner
  status?: 'Todo' | 'InProgress' | 'Done'; // For weekly planner
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({
  isOpen,
  onClose,
  onTaskAdded,
  mode,
  currentWeekDates = [],
  defaultDay = 1,
  task, // Add task prop for edit mode
  spaceId,
  initialDueDate,
  presetAgentId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    priority: 'Medium',
    category: 'App Idea',
    estimatedTime: '1h',
    tags: '',
    dueDate: '',
    day: defaultDay,
    status: 'Todo'
  });

  // ── Agent attachment (task-management + space-task modes) ────────────────
  // Multi-select — agentIds is the authoritative list; the first entry drives
  // the single-agent affordances (notify override, "Run agent now") since
  // those are inherently one-run-at-a-time actions, not per-agent.
  const [agents, setAgents] = useState<any[]>([]);
  const [agentIds, setAgentIds] = useState<string[]>(presetAgentId ? [presetAgentId] : []);
  const [notifyOverride, setNotifyOverride] = useState<boolean>(false);
  const [channelCheck, setChannelCheck] = useState<Record<string, 'checking' | 'connected' | 'disconnected'>>({});
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState<{ agentRunId?: string; status?: string } | null>(null);

  // ── Team attachment (task-management mode only — space-task mode already
  // has an implicit single team via the spaceId prop) ──────────────────────
  const [teams, setTeams] = useState<any[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [assigneeId, setAssigneeId] = useState('');

  // ── Attach to an existing task instead of creating a new one — offered
  // only when opening this modal from an agent's or a team's own page ──────
  const canAttachExisting = !task && (!!presetAgentId || mode === 'space-task');
  const [attachMode, setAttachMode] = useState(false);
  const [existingTasks, setExistingTasks] = useState<any[]>([]);
  const [existingTasksLoading, setExistingTasksLoading] = useState(false);
  const [selectedExistingTaskId, setSelectedExistingTaskId] = useState('');

  const selectedAgents = agents.filter((a: any) => agentIds.includes(a._id || a.id));
  const selectedAgent = selectedAgents[0] || null;

  useEffect(() => {
    if ((mode !== 'task-management' && mode !== 'space-task') || !isOpen) return;
    customAgentAPI.list()
      .then((d: any) => setAgents(Array.isArray(d) ? d : d?.agents || []))
      .catch(() => {});
  }, [mode, isOpen]);

  useEffect(() => {
    if (mode !== 'task-management' || !isOpen) return;
    spacesAPI.list({ filter: 'my' })
      .then((d: any) => setTeams(Array.isArray(d?.spaces) ? d.spaces : []))
      .catch(() => setTeams([]));
  }, [mode, isOpen]);

  useEffect(() => {
    if (!canAttachExisting || !attachMode || !isOpen) return;
    setExistingTasksLoading(true);
    taskService.getTasks()
      .then((t) => setExistingTasks(Array.isArray(t) ? t : []))
      .catch(() => setExistingTasks([]))
      .finally(() => setExistingTasksLoading(false));
  }, [canAttachExisting, attachMode, isOpen]);

  // Re-verify channel connectivity for the attached agent's notify channels
  // whenever the override is turned on or the selected agent changes.
  useEffect(() => {
    if (!notifyOverride || !selectedAgent) { setChannelCheck({}); return; }
    const channels: string[] = selectedAgent.notificationChannels || [];
    let alive = true;
    (async () => {
      const next: Record<string, 'checking' | 'connected' | 'disconnected'> = {};
      for (const c of channels) next[c] = 'checking';
      setChannelCheck(next);
      const results = await Promise.all(channels.map(async (c) => [c, await isChannelConnected(c as 'email' | 'telegram')] as const));
      if (!alive) return;
      const resolved: Record<string, 'checking' | 'connected' | 'disconnected'> = {};
      for (const [c, ok] of results) resolved[c] = ok ? 'connected' : 'disconnected';
      setChannelCheck(resolved);
    })();
    return () => { alive = false; };
  }, [notifyOverride, selectedAgent]);

  const blockedChannels = Object.entries(channelCheck).filter(([, s]) => s === 'disconnected').map(([c]) => c);

  const handleRunAgentNow = async () => {
    if (!selectedAgent) return;
    setRunning(true);
    try {
      const agentDbId = selectedAgent._id || selectedAgent.id;
      const prompt = `${formData.title}${formData.description ? `\n\n${formData.description}` : ''}`;
      const data: any = await customAgentAPI.execute(agentDbId, {
        prompt,
        notifyOnCompletion: notifyOverride,
        notificationChannels: selectedAgent.notificationChannels || [],
      });
      setLastRun({ agentRunId: data?.agentRunId, status: 'success' });
      toast.success(`"${selectedAgent.name}" ran successfully`);
    } catch (err: any) {
      setLastRun({ status: 'error' });
      toast.error(err?.response?.data?.error || err?.message || 'Agent run failed');
    } finally {
      setRunning(false);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (task) {
        setFormData({
          title: task.title || '',
          description: task.description || '',
          priority: task.priority || 'Medium',
          category: task.category || 'App Idea',
          estimatedTime: task.estimatedTime || '1h',
          tags: task.tags || '',
          dueDate: task.dueDate ? task.dueDate.slice(0, 10) : '', // Fix: format for input type="date"
          day: task.day || defaultDay,
          status: task.status || 'Todo'
        });
      } else {
        setFormData({
          title: '',
          description: '',
          priority: 'Medium',
          category: 'App Idea',
          estimatedTime: '1h',
          tags: '',
          dueDate: initialDueDate ? initialDueDate.slice(0, 10) : '',
          day: defaultDay,
          status: 'Todo'
        });
      }
      const taskAny = task as any;
      const initialAgentIds: string[] = Array.isArray(taskAny?.agentIds) && taskAny.agentIds.length
        ? taskAny.agentIds.map((a: any) => (typeof a === 'string' ? a : a._id))
        : (taskAny?.agentId ? [taskAny.agentId] : (presetAgentId ? [presetAgentId] : []));
      setAgentIds(initialAgentIds);
      const initialTeamIds: string[] = Array.isArray(taskAny?.spaceIds) && taskAny.spaceIds.length
        ? taskAny.spaceIds.map((s: any) => (typeof s === 'string' ? s : s._id))
        : (taskAny?.spaceId ? [taskAny.spaceId] : []);
      setTeamIds(initialTeamIds);
      setAssigneeId(String(taskAny?.assignedToUserId?._id || taskAny?.assignedToUserId || ''));
      setNotifyOverride(taskAny?.notifyOnCompletion ?? false);
      setLastRun(taskAny?.agentRunId ? { agentRunId: taskAny.agentRunId, status: taskAny.agentRunStatus } : null);
      setAttachMode(false);
      setSelectedExistingTaskId('');
      setError(null);
    }
  }, [isOpen, defaultDay, task, initialDueDate, presetAgentId]);

  // Toggling an agent on defaults the notify override to *that* agent's
  // saved setting only when it's the first one selected (the notify/run-now
  // affordances are inherently single-agent, driven by selectedAgent above).
  const toggleAgent = (id: string) => {
    setAgentIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (!prev.includes(id) && prev.length === 0) {
        const agent = agents.find((a: any) => (a._id || a.id) === id);
        setNotifyOverride(!!agent?.notifyOnCompletion);
      }
      setLastRun(null);
      return next;
    });
  };

  const toggleTeam = (id: string) => {
    setAssigneeId('');
    setTeamIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }
    if (mode === 'space-task' && agentIds.length === 0) {
      setError('Select at least one agent to drive this task');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Space tasks go through a dedicated endpoint (spaceId + createdBy +
      // agentIds server-side, gated to owner/manager) — same Task collection
      // the personal Kanban board reads, so it's automatically in sync.
      if (mode === 'space-task') {
        if (!spaceId) throw new Error('spaceId is required');
        const res: any = await spacesAPI.createKanbanTask(spaceId, {
          title: formData.title.trim(),
          description: formData.description || '',
          priority: formData.priority,
          category: formData.category,
          dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : undefined,
          estimatedTime: formData.estimatedTime,
          tags: formData.tags || '',
          agentIds,
        });
        onTaskAdded(res?.task);
        onClose();
        return;
      }

      // Prepare task data based on mode
      const taskData: CreateTaskData = {
        title: formData.title.trim(),
        description: formData.description || '',
        priority: formData.priority,
        category: formData.category,
        estimatedTime: formData.estimatedTime,
        tags: formData.tags || ''
      };

      if (mode === 'weekly-planner') {
        // For weekly planner, set due date based on selected day and current week
        if (currentWeekDates && currentWeekDates[formData.day! - 1]) {
          taskData.dueDate = currentWeekDates[formData.day! - 1].toISOString();
        }
        taskData.day = formData.day;
        taskData.status = formData.status;
      } else {
        // For task management, use the due date directly
        if (formData.dueDate) {
          taskData.dueDate = new Date(formData.dueDate).toISOString();
        }
        // Agent/team attachment — metadata + per-run notify override only;
        // saving never triggers a run on its own (see "Run agent now" button).
        taskData.agentIds = agentIds;
        taskData.spaceIds = teamIds;
        if (assigneeId && teamIds.length === 1) { taskData.assignmentTeamId = teamIds[0]; taskData.assignedToUserId = assigneeId; }
        taskData.notifyOnCompletion = agentIds.length ? notifyOverride : null;
        if (lastRun?.agentRunId) {
          taskData.agentRunId = lastRun.agentRunId;
          taskData.agentRunStatus = lastRun.status || null;
        }
      }

      let resultTask;
      if (task && task._id) {
        // Edit mode: update existing task
        resultTask = await taskService.updateTask(task._id, taskData);
        if (assigneeId && teamIds.length === 1 && (String((task as any).assignedToUserId?._id || (task as any).assignedToUserId || '') !== assigneeId || (task as any).assignmentStatus === 'declined')) {
          resultTask = await taskService.assignTask(task._id, teamIds[0], assigneeId);
        }
      } else {
        // Add mode: create new task
        resultTask = await taskService.createTask(taskData);
      }

      onTaskAdded(resultTask);
      onClose();
    } catch (err) {
      console.error('Error creating/updating task:', err);
      setError(err instanceof Error ? err.message : 'Failed to create/update task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Attach an already-existing task (picked from the dropdown) to whichever
  // agent(s)/team are currently selected in the form, instead of creating a
  // duplicate task. Additive — never removes an existing assignment.
  const handleAttachExisting = async () => {
    if (!selectedExistingTaskId) {
      setError('Select a task to attach');
      return;
    }
    const targets: { agentId?: string; spaceId?: string }[] = [
      ...agentIds.map((id) => ({ agentId: id })),
      ...(mode === 'space-task' && spaceId ? [{ spaceId }] : []),
      ...teamIds.map((id) => ({ spaceId: id })),
    ];
    if (!targets.length) {
      setError('Select at least one agent or team to attach this task to');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let result: any = null;
      for (const target of targets) {
        result = await taskService.attachTask(selectedExistingTaskId, target);
      }
      onTaskAdded(result);
      onClose();
    } catch (err) {
      console.error('Error attaching task:', err);
      setError(err instanceof Error ? err.message : 'Failed to attach task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // In category dropdown, use these options
  const categoryOptions = [
    'App Idea', 'Web App', 'Social App', 'EdTech', 'Business Idea', 'Product Concept', 'Feature Request', 'Research Topic', 'Creative Project', 'Other'
  ];

  const isEditMode = !!task;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{isEditMode ? 'Edit Task' : attachMode ? 'Attach Existing Task' : 'Add New Task'}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {canAttachExisting && (
          <div className="flex gap-2 mb-4 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => setAttachMode(false)}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${!attachMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
              disabled={loading}
            >
              Create new task
            </button>
            <button
              type="button"
              onClick={() => setAttachMode(true)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${attachMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
              disabled={loading}
            >
              <Link2 className="w-3.5 h-3.5" /> Attach existing task
            </button>
          </div>
        )}

        {attachMode ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Existing task</label>
              <select
                value={selectedExistingTaskId}
                onChange={(e) => setSelectedExistingTaskId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading || existingTasksLoading}
              >
                <option value="">{existingTasksLoading ? 'Loading your tasks…' : 'Select a task…'}</option>
                {existingTasks.map((t: any) => (
                  <option key={t._id} value={t._id}>{t.title}</option>
                ))}
              </select>
              {!existingTasksLoading && existingTasks.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">You don't have any tasks yet — create one instead.</p>
              )}
            </div>
          </div>
        ) : (
        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter task title..."
              disabled={loading}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Enter task description..."
              disabled={loading}
            />
          </div>

          {/* Priority and Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'Low' | 'Medium' | 'High' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as 'App Idea' | 'Web App' | 'Social App' | 'EdTech' | 'Business Idea' | 'Product Concept' | 'Feature Request' | 'Research Topic' | 'Creative Project' | 'Other' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                {categoryOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date/Day and Estimated Time */}
          <div className="grid grid-cols-2 gap-4">
            {mode === 'task-management' || mode === 'space-task' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Day of Week
                </label>
                <select
                  value={formData.day}
                  onChange={(e) => setFormData({ ...formData, day: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                  <option value={7}>Sunday</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Time
              </label>
              <select
                value={formData.estimatedTime}
                onChange={(e) => setFormData({ ...formData, estimatedTime: e.target.value as '0.5h' | '1h' | '2h' | '4h' | '8h' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="0.5h">30 minutes</option>
                <option value="1h">1 hour</option>
                <option value="2h">2 hours</option>
                <option value="4h">4 hours</option>
                <option value="8h">8 hours</option>
              </select>
            </div>
          </div>

          {/* Status (only for weekly planner) */}
          {mode === 'weekly-planner' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Todo' | 'InProgress' | 'Done' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="Todo">📋 Todo</option>
                <option value="InProgress">⏳ In Progress</option>
                <option value="Done">✅ Done</option>
              </select>
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Frontend, React, Urgent"
              disabled={loading}
            />
          </div>
        </div>
        )}

        {/* Agent + Team attachment — shown in both "create" and "attach
            existing" modes, since attach mode needs to know what to attach
            the picked task to. task-management: optional; space-task:
            at least one agent required (the space's "manager" role drives
            the agent(s) that own this task). */}
        {(mode === 'task-management' || mode === 'space-task') && (
          <div className="space-y-4 mt-4">
            <div className="border-t border-gray-200 pt-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Bot className="w-4 h-4" /> Agent{mode === 'space-task' ? 's *' : 's (optional)'}
              </label>
              <div className="border border-gray-300 rounded-lg max-h-36 overflow-y-auto divide-y divide-gray-100">
                {agents.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-400">No agents found</div>
                ) : (
                  agents.map((a: any) => {
                    const id = a._id || a.id;
                    const checked = agentIds.includes(id);
                    return (
                      <label key={id} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" checked={checked} onChange={() => toggleAgent(id)} disabled={loading} />
                        {a.name}
                      </label>
                    );
                  })
                )}
              </div>

              {selectedAgent && (
                <div className="mt-3 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={notifyOverride}
                      onChange={(e) => setNotifyOverride(e.target.checked)}
                      disabled={loading}
                    />
                    Notify me for this run
                  </label>

                  {blockedChannels.map((c) => (
                    <div key={c} className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="flex-1">Connect {CHANNEL_LABELS[c] || c} to receive notifications from this agent.</span>
                      <a href={`/integrations?connect=${c}`} target="_blank" rel="noopener noreferrer" className="font-semibold underline whitespace-nowrap">
                        Connect
                      </a>
                    </div>
                  ))}

                  {mode === 'task-management' && (
                    <>
                      <button
                        type="button"
                        onClick={handleRunAgentNow}
                        disabled={running || (notifyOverride && blockedChannels.length > 0)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        {running ? 'Running…' : 'Run agent now'}
                      </button>

                      {lastRun && (
                        <div className={`text-xs ${lastRun.status === 'error' ? 'text-red-600' : 'text-green-600'}`}>
                          {lastRun.status === 'error' ? 'Last run failed.' : 'Last run completed successfully.'}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* My Teams (task-management mode only — space-task already has
                an implicit single team via the spaceId prop) */}
            {mode === 'task-management' && (
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Users className="w-4 h-4" /> My Teams (optional)
                </label>
                <div className="border border-gray-300 rounded-lg max-h-36 overflow-y-auto divide-y divide-gray-100">
                  {teams.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-400">No teams found</div>
                  ) : (
                    teams.map((t: any) => {
                      const checked = teamIds.includes(t._id);
                      return (
                        <label key={t._id} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50">
                          <input type="checkbox" checked={checked} onChange={() => toggleTeam(t._id)} disabled={loading} />
                          {t.name}
                        </label>
                      );
                    })
                  )}
                </div>
                {!attachMode && teamIds.length > 0 && (
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <label htmlFor="task-assignee" className="block text-sm font-semibold text-gray-800 mb-1">Assign to team member</label>
                    {teamIds.length > 1 ? <p className="text-xs text-amber-700">Choose one team to assign a person. Multiple teams can still be attached without assigning.</p> : (
                      <>
                        <select id="task-assignee" className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white" value={assigneeId} onChange={e => setAssigneeId(e.target.value)}>
                          <option value="">Unassigned — team reference only</option>
                          {(teams.find((t: any) => t._id === teamIds[0])?.members || []).filter((m: any) => m.userId).map((m: any) => {
                            const id = String(m.userId?._id || m.userId);
                            return <option key={id} value={id}>{m.lyranName || m.userId?.name || m.userId?.email || id}</option>;
                          })}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">The assignee will see this task in Pending Assignments until accepted.</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={attachMode ? handleAttachExisting : handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 btn-primary"
            disabled={loading || (attachMode ? !selectedExistingTaskId : (!formData.title.trim() || (mode === 'space-task' && agentIds.length === 0)))}
          >
            {attachMode
              ? (loading ? 'Attaching...' : 'Attach Task')
              : (loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Task' : 'Add Task'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTaskModal;
